import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, MessageCircle } from "lucide-react";
import { COMPANY, SERVICES, BUDGET_OPTIONS, waLink } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name"),
  phone: z.string().min(7, "Please enter a valid phone number"),
  email: z.string().email("Please enter a valid email address"),
  postcode: z.string().min(2, "Please enter your postcode or location"),
  service: z.string().min(1, "Please select a service"),
  budget: z.string().optional(),
  description: z.string().min(10, "Please tell us a little more about your project"),
  privacy_consent: z.literal(true, { errorMap: () => ({ message: "Please confirm you agree to our privacy policy" }) }),
});

const inputCls =
  "w-full rounded-xl border border-brand-light bg-white px-4 py-3 text-sm text-brand-ink outline-none transition-all placeholder:text-brand-ink/35 focus:border-brand-green focus:ring-2 focus:ring-brand-green/15";
const errCls = "mt-1.5 text-xs font-semibold text-brand-red";

export const QuoteForm = ({ defaultService = "" }) => {
  const [done, setDone] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { service: defaultService, budget: "" },
  });

  const onSubmit = async (values) => {
    try {
      const { data } = await axios.post(`${API}/enquiries`, { ...values, budget: values.budget || null });
      setDone(data.id);
      toast.success("Your enquiry has been sent. We'll be in touch shortly.");
    } catch (e) {
      toast.error("Sorry — your enquiry could not be sent. Please call or WhatsApp us instead.");
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="rounded-2xl border border-brand-green/20 bg-brand-light/60 p-10 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-brand-green" />
        <h3 className="mt-4 font-heading text-2xl font-bold text-brand-green">Thank you — enquiry received.</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-brand-muted">
          Your reference is <span className="font-bold text-brand-ink">{done.slice(0, 8).toUpperCase()}</span>. {COMPANY.contact} will
          contact you shortly to discuss your project and arrange a free quote.
        </p>
        <p className="mt-5 text-sm text-brand-muted">
          Need us sooner?{" "}
          <a href={`tel:${COMPANY.phone}`} className="font-bold text-brand-green underline underline-offset-2">Call {COMPANY.phoneDisplay}</a>
        </p>
      </div>
    );
  }

  return (
    <form data-testid="quote-form" onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-5 sm:grid-cols-2">
      <div>
        <label htmlFor="full_name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Full name *</label>
        <input id="full_name" data-testid="quote-name-input" autoComplete="name" placeholder="Your full name" className={inputCls} {...register("full_name")} />
        {errors.full_name && <p data-testid="error-full-name" className={errCls}>{errors.full_name.message}</p>}
      </div>
      <div>
        <label htmlFor="phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Phone number *</label>
        <input id="phone" data-testid="quote-phone-input" type="tel" autoComplete="tel" placeholder="07xxx xxxxxx" className={inputCls} {...register("phone")} />
        {errors.phone && <p data-testid="error-phone" className={errCls}>{errors.phone.message}</p>}
      </div>
      <div>
        <label htmlFor="email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Email address *</label>
        <input id="email" data-testid="quote-email-input" type="email" autoComplete="email" placeholder="you@example.com" className={inputCls} {...register("email")} />
        {errors.email && <p data-testid="error-email" className={errCls}>{errors.email.message}</p>}
      </div>
      <div>
        <label htmlFor="postcode" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Postcode / location *</label>
        <input id="postcode" data-testid="quote-postcode-input" autoComplete="postal-code" placeholder="e.g. HA3 7AB" className={inputCls} {...register("postcode")} />
        {errors.postcode && <p data-testid="error-postcode" className={errCls}>{errors.postcode.message}</p>}
      </div>
      <div>
        <label htmlFor="service" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Service required *</label>
        <select id="service" data-testid="quote-service-select" className={inputCls} {...register("service")}>
          <option value="">Select a service…</option>
          {SERVICES.map((s) => (
            <option key={s.id} value={s.title}>{s.title}</option>
          ))}
          <option value="Other / Not sure">Other / Not sure</option>
        </select>
        {errors.service && <p data-testid="error-service" className={errCls}>{errors.service.message}</p>}
      </div>
      <div>
        <label htmlFor="budget" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Estimated budget (optional)</label>
        <select id="budget" data-testid="quote-budget-select" className={inputCls} {...register("budget")}>
          <option value="">Prefer not to say</option>
          {BUDGET_OPTIONS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="description" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Project description *</label>
        <textarea id="description" data-testid="quote-description-input" rows={4} placeholder="Tell us about your project — what you'd like done, timescales, anything helpful…" className={inputCls} {...register("description")} />
        {errors.description && <p data-testid="error-description" className={errCls}>{errors.description.message}</p>}
      </div>
      <div className="sm:col-span-2">
        <label className="flex cursor-pointer items-start gap-3 text-sm text-brand-muted">
          <input type="checkbox" data-testid="quote-privacy-checkbox" className="mt-0.5 h-4 w-4 rounded border-brand-light accent-brand-green" {...register("privacy_consent")} />
          <span>
            I agree to Krishnaa Constructions storing my details so they can respond to my enquiry, as described in the{" "}
            <a href="/privacy" className="font-semibold text-brand-green underline underline-offset-2">Privacy Policy</a>. *
          </span>
        </label>
        {errors.privacy_consent && <p data-testid="error-privacy" className={errCls}>{errors.privacy_consent.message}</p>}
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={isSubmitting}
          data-testid="quote-form-submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-red px-8 py-4 text-base font-bold text-white shadow-lg shadow-brand-red/25 transition-all hover:bg-brand-redDark hover:shadow-xl active:scale-[0.98] disabled:opacity-60 sm:w-auto"
        >
          {isSubmitting && <Loader2 className="h-5 w-5 animate-spin" />}
          {isSubmitting ? "Sending…" : "Request a Free Quote"}
        </button>
        <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-brand-muted">
          Prefer to talk?
          <a href={`tel:${COMPANY.phone}`} data-testid="quote-call-alt" className="inline-flex items-center gap-1.5 font-bold text-brand-green"><Phone className="h-3.5 w-3.5" /> {COMPANY.phoneDisplay}</a>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="quote-whatsapp-alt" className="inline-flex items-center gap-1.5 font-bold text-brand-green"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp us</a>
        </p>
      </div>
    </form>
  );
};
